/**
 * Created by neo on 01.12.16.
 */

import { reaction, runInAction, makeAutoObservable } from 'mobx';
import { CoachWorkout, createCoachWorkout } from '../Model/Coach/Program/Workout/CoachWorkout';
import { ViewWorkout, createViewWorkout } from '../Model/WorkoutLog/ViewWorkout';
import { WorkoutLog, createWorkoutLog } from '../Model/WorkoutLog/WorkoutLog';
import { AuthenticationStore } from './AuthenticationStore';
import { AnalyticsStore } from './Firebase/AnalyticsStore';
import { Athlete } from '../Model/Athlete/Athlete';
import { MicroCycleDay } from '../Model/Coach/Program/MicroCycleDay';
import { EventEntry } from '../Model/Coach/Schedule/EventEntry';
import { ViewBlock } from '../Model/WorkoutLog/ViewBlock';
import { ViewSet } from '../Model/WorkoutLog/ViewSet';

/**
 * This class is a wrapper for a WorkoutRun and a singleton.
 */
function createWorkoutRunner(authentication: AuthenticationStore, analytics: AnalyticsStore) {
  const store = makeAutoObservable({
    authentication,
    analytics,
    viewWorkout: createViewWorkout(createWorkoutLog(), createCoachWorkout()),

    prepareWorkout(
      athlete: Athlete,
      workout: CoachWorkout,
      microCycleDay?: MicroCycleDay,
      eventEntry?: EventEntry,
    ): Promise<ViewWorkout> {
      const workoutLog = createWorkoutLog({
        athlete,
        coachProgramId: microCycleDay?.athleteSuperMacroId,
        microCycleDay,
        workout,
        mesoCycleId: microCycleDay?.mesoCycleId,
        microCycleId: microCycleDay?.microCycleId,
        microCycleDayId: microCycleDay?.id,
        workoutId: workout.id,
        eventEntry,
      } as any);
      return Promise.resolve(microCycleDay?.workout ?? workout)
        .then((result) => createViewWorkout(workoutLog, result ?? createCoachWorkout()))
        .then((viewWorkout) => {
          runInAction(() => (this.viewWorkout = viewWorkout));
          return viewWorkout;
        });
    },

    replay(workoutLog: WorkoutLog, workout: CoachWorkout) {
      this.viewWorkout = createViewWorkout(workoutLog, workout);
    },

    startExercise(viewBlock: ViewBlock, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      const { workoutLog } = this;

      if (!workoutLog.started) {
        this.analytics.logEvent('Workout_Started', {
          startDateTime: (startDateTime ?? new Date())?.toISOString(),
          workoutLogId: workoutLog.id,
          workoutId: workoutLog.workoutId,
          workoutType: workoutLog.workout?.type,
          workoutTemplateId: workoutLog.workout?.scriptId,
          name: workoutLog.workout?.defaultName?.substring(0, 40),
        });
      }

      if (!this.workoutLog.ended) {
        this.workoutLog.start();
        if (viewBlock) {
          return viewBlock.start(startDateTime, save);
        }
        return undefined;
      }
      return undefined;
    },

    startSet(viewSet: ViewSet, startDateTime: Date = new Date(), save: boolean = true): ViewSet | undefined {
      const { workoutLog } = this;
      if (!workoutLog.started) {
        this.analytics.logEvent('Workout_Started', {
          startDateTime: startDateTime?.toISOString() ?? new Date().toISOString(),
          workoutLogId: workoutLog.id,
          workoutId: workoutLog.workoutId,
          workoutType: workoutLog.workout?.type,
          workoutTemplateId: workoutLog.workout?.scriptId,
          name: workoutLog.workout?.defaultName?.substring(0, 40),
        });
      }

      if (!this.workoutLog.ended) {
        this.workoutLog.start();
        return viewSet.viewBlock.startSet(viewSet, startDateTime, save);
      }
      return undefined;
    },

    endWorkout(): WorkoutLog {
      const { workoutLog } = this;
      this.workoutLog.stopExercise(false);

      if (!workoutLog.endDateTime) {
        const { completedExercises, totalExercises } = this;
        const completedRatio = Math.min(1, completedExercises / totalExercises);
        this.analytics.logEvent('Workout_finished', {
          timestamp: Date.now(),
          workoutLogId: workoutLog.id,
          workoutId: workoutLog.workoutId,
          workoutType: workoutLog.workout?.type,
          workoutTemplateId: workoutLog.workout?.scriptId,
          completedExercises,
          totalExercises,
          durationInSeconds: workoutLog.durationInSeconds,
          percentageCompleted: Math.round(completedRatio * 1000) / 10,
          name: workoutLog.workout?.defaultName?.substring(0, 40),
        });
      }

      return this.viewWorkout?.end() ?? this.workoutLog;
    },

    terminateAndSave(): Promise<WorkoutLog> {
      this.endWorkout();
      return this.viewWorkout.terminateAndSave();
    },

    resetWorkout() {
      this.viewWorkout = createViewWorkout(createWorkoutLog(), createCoachWorkout());
    },

    get workoutLog(): WorkoutLog {
      return this.viewWorkout.workoutLog;
    },

    get isBodyWeightWorkout() {
      return this.viewWorkout.isBodyWeight;
    },

    get activeViewSet(): ViewSet | undefined {
      return this.viewWorkout.activeViewSet;
    },

    get completedExercises(): number {
      return this.viewWorkout.completedExercises;
    },

    get totalExercises(): number {
      return this.viewWorkout.totalExercises;
    },
  });

  reaction(
    () => authentication.athleteId,
    (athleteId) => {
      if (!athleteId) {
        store.viewWorkout = createViewWorkout(createWorkoutLog(), createCoachWorkout());
      }
    },
  );

  return store;
}

export type WorkoutRunner = ReturnType<typeof createWorkoutRunner>;
export const createWorkoutRunnerInstance = createWorkoutRunner;
